/**
 * Presentation helpers for the supplier dashboard. These never change which
 * periods are open or which requirements apply; they only derive what to show
 * from data the dashboard already loads.
 */
import { format } from 'date-fns';
import { documentDeadline } from '../../utils/documentDeadline';
import { DisplayStatus, toDisplayStatus } from '../../utils/statusMeta';
import { getIntervalMonths, isOverrideApplicable, UserRequirementOverride } from '../../utils/overrideUtils';

export const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
export const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
export const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const REVISION_PREFIX = 'SOLICITUD DE REVISIÓN:';

export interface SupplierDocument {
  id: string;
  status: string;
  rejection_reason: string | null;
  upload_date: string;
  applicable_month?: string | null;
  applicable_year?: number | null;
  is_deleted?: boolean | null;
}

const isActive = (doc: SupplierDocument) => !doc.is_deleted;
const isReplaced = (doc: SupplierDocument) => doc.status === 'reemplazado' || doc.status === 'replaced';

/** Most recent non-deleted version (by upload date), matching the dashboard's existing rule. */
export function latestActiveDocument<T extends SupplierDocument>(docs: T[] | null | undefined): T | null {
  const active = (docs || []).filter(isActive);
  if (active.length === 0) return null;
  return active.reduce((latest, current) => (new Date(current.upload_date) > new Date(latest.upload_date) ? current : latest));
}

/**
 * Display status of one uploaded version. A version still pending approval
 * that carries a reviewer comment is a correction request.
 */
export function documentDisplayStatus(doc: Pick<SupplierDocument, 'status' | 'rejection_reason'>): DisplayStatus {
  if (doc.status === 'pending_approval' && doc.rejection_reason) return 'correction_requested';
  return toDisplayStatus(doc.status);
}

/** Reviewer comment without the internal revision-request prefix. */
export function reviewerReason(doc: Pick<SupplierDocument, 'status' | 'rejection_reason'> | null | undefined): string | null {
  if (!doc?.rejection_reason) return null;
  const status = documentDisplayStatus(doc);
  if (status !== 'rejected' && status !== 'correction_requested') return null;
  const text = doc.rejection_reason.startsWith(REVISION_PREFIX)
    ? doc.rejection_reason.slice(REVISION_PREFIX.length)
    : doc.rejection_reason;
  return text.trim() || null;
}

/** Symbol used in the dense annual grid. Each status has its own glyph. */
export const STATUS_SYMBOL: Partial<Record<DisplayStatus, string>> = {
  approved: '✓',
  pending_approval: '◷',
  correction_requested: '↻',
  rejected: '✕',
  overdue: '!',
  expired: '!',
  missing: '—',
};

/** Annual-grid cell status from the cell's winning document status. */
export function cellDisplayStatus(rawStatus: string, rejectionReason?: string | null): DisplayStatus {
  switch (rawStatus) {
    case 'approved':
      return 'approved';
    case 'pending_approval':
      return rejectionReason ? 'correction_requested' : 'pending_approval';
    case 'rejected':
      return 'rejected';
    case 'expired':
      return 'expired';
    default:
      return 'missing';
  }
}

export interface UploadAvailability {
  blocked: boolean;
  /** First day on which the period accepts uploads, when it is blocked. */
  availableFrom: Date | null;
}

/**
 * Same rule the dashboard has always applied to requirements without
 * documents: monthly / custom-interval periods open the month after the
 * requirement's due date. Returns the opening date so the UI can explain it.
 */
export function getUploadAvailability(
  requirement: { due_date: string | null; definition: { renewal_type: string; renewal_interval: string | null } },
  today: Date = new Date(),
): UploadAvailability {
  const renewalType = requirement.definition.renewal_type;
  const renewalInterval = requirement.definition.renewal_interval;
  if (!requirement.due_date) return { blocked: false, availableFrom: null };

  const dueDate = new Date(requirement.due_date);
  const dueDateMonth = dueDate.getMonth();
  const dueDateYear = dueDate.getFullYear();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  let applies = false;
  if (renewalType === 'monthly') {
    applies = true;
  } else if (renewalType === 'custom_interval' && renewalInterval) {
    let intervalMonths = 0;
    if (typeof renewalInterval === 'string') {
      const match = renewalInterval.match(/(\d+)\s*mons?/);
      if (match) intervalMonths = parseInt(match[1]);
    }
    applies = intervalMonths > 0;
  }
  if (!applies) return { blocked: false, availableFrom: null };

  const enableMonth = dueDateMonth + 1;
  const enableYear = enableMonth > 11 ? dueDateYear + 1 : dueDateYear;
  const enableMonthAdjusted = enableMonth > 11 ? enableMonth - 12 : enableMonth;
  const blocked = currentYear < enableYear || (currentYear === enableYear && currentMonth < enableMonthAdjusted);
  return { blocked, availableFrom: blocked ? new Date(enableYear, enableMonthAdjusted, 1) : null };
}

/** Human period name for a period of `cadence` months starting at `startMonth` (1-based). */
export function periodLabel(cadence: number | null, year: number, startMonth: number): string {
  if (!cadence || cadence === 1) return `${MONTH_NAMES[startMonth - 1]} ${year}`;
  if (cadence >= 12) return `Año ${year}`;
  const end = Math.min(startMonth + cadence - 1, 12);
  return `${MONTH_SHORT[startMonth - 1]}–${MONTH_SHORT[end - 1]} ${year}`;
}

/** yyyy-MM of a version: its applicable period, else its upload month (same fallback as the annual grid). */
export function documentMonthKey(doc: SupplierDocument): string {
  const month = doc.applicable_month ? MONTH_CODES.indexOf(doc.applicable_month) + 1 : 0;
  if (month && doc.applicable_year) return `${doc.applicable_year}-${String(month).padStart(2, '0')}`;
  return format(new Date(doc.upload_date), 'yyyy-MM');
}

// ─── Atención requerida ─────────────────────────────────────

export interface AttentionDefinition {
  id: string;
  name: string;
  renewal_type: string;
  renewal_interval: string | null;
  category?: { name: string } | null;
}

export interface AttentionItem {
  key: string;
  definitionId: string;
  documentName: string;
  period: string;
  status: DisplayStatus;
  reason: string | null;
  /** Month (yyyy-MM) to open in the list to act on the item. */
  targetMonth: string;
  documentId?: string;
  deadline?: string;
}

export interface AttentionInput {
  definitions: AttentionDefinition[];
  requirements: Array<{ definition_id: string; documents?: SupplierDocument[] | null }>;
  overrides?: Array<Pick<UserRequirementOverride, 'definition_id' | 'renewal_type' | 'renewal_interval'>>;
  today?: Date;
  /** Periods that ended before this date (e.g. before the supplier's account existed) are not flagged as missing. */
  since?: string | null;
}

const SEVERITY: Partial<Record<DisplayStatus, number>> = { rejected: 0, correction_requested: 1, overdue: 2 };

const validCadence = (months: number | null) => (months && months >= 1 && months <= 12 && 12 % months === 0 ? months : null);

const periodStartOf = (month: number, cadence: number) => Math.floor((month - 1) / cadence) * cadence + 1;

/** Month to open for a period, following the annual grid's click convention. */
const targetMonthFor = (cadence: number, year: number, start: number) => {
  const month = cadence === 1 ? start : cadence >= 12 ? 1 : Math.min(start + cadence - 1, 12);
  return `${year}-${String(month).padStart(2, '0')}`;
};

/**
 * Items the supplier has to act on, across every period the dashboard knows:
 * the latest version of a period was rejected or has a correction request, or
 * the period has no delivery and its deadline (documentDeadline, 1 month of
 * grace like the list) already passed during the current year.
 */
export function buildAttentionItems({ definitions, requirements, overrides = [], today = new Date(), since }: AttentionInput): AttentionItem[] {
  const todayKey = format(today, 'yyyy-MM-dd');
  const currentYear = today.getFullYear();
  const sinceKey = since ? format(new Date(since), 'yyyy-MM-dd') : null;
  const docsByDefinition = new Map<string, SupplierDocument[]>();
  requirements.forEach((req) => {
    const list = docsByDefinition.get(req.definition_id) || [];
    list.push(...(req.documents || []).filter((doc) => isActive(doc) && !isReplaced(doc)));
    docsByDefinition.set(req.definition_id, list);
  });

  const items: AttentionItem[] = [];
  definitions.forEach((definition) => {
    const docs = docsByDefinition.get(definition.id) || [];
    const cadence = validCadence(getIntervalMonths(definition.renewal_type, definition.renewal_interval));

    // 1. Rejected / correction requested: latest version of each period.
    const byPeriod = new Map<string, { docs: SupplierDocument[]; year: number; start: number }>();
    docs.forEach((doc) => {
      const [year, month] = documentMonthKey(doc).split('-').map(Number);
      const start = cadence ? periodStartOf(month, cadence) : month;
      const key = cadence ? `${year}-${start}` : 'single';
      const group = byPeriod.get(key) || { docs: [], year, start };
      group.docs.push(doc);
      byPeriod.set(key, group);
    });
    byPeriod.forEach((group, key) => {
      const latest = latestActiveDocument(group.docs);
      if (!latest) return;
      const status = documentDisplayStatus(latest);
      if (status !== 'rejected' && status !== 'correction_requested') return;
      items.push({
        key: `${definition.id}:${key}:${status}`,
        definitionId: definition.id,
        documentName: definition.name,
        period: periodLabel(cadence, group.year, group.start),
        status,
        reason: reviewerReason(latest),
        targetMonth: documentMonthKey(latest),
        documentId: latest.id,
      });
    });

    // 2. Missing past the deadline. A per-supplier override with a longer
    // cadence means one delivery covers the whole override period.
    if (!['monthly', 'yearly', 'custom_interval'].includes(definition.renewal_type) || !cadence) return;
    const override = overrides.find((o) => o.definition_id === definition.id);
    const useOverride = override && isOverrideApplicable(override, definition.renewal_type, definition.renewal_interval);
    const periodCadence = useOverride ? validCadence(getIntervalMonths(override!.renewal_type, override!.renewal_interval)) : cadence;
    if (!periodCadence) return;
    const deadlineType = useOverride ? override!.renewal_type : definition.renewal_type;
    const deadlineInterval = useOverride ? override!.renewal_interval : definition.renewal_interval;
    const deliveredMonths = new Set(docs.map(documentMonthKey));

    for (const year of [currentYear - 1, currentYear]) {
      for (let start = 1; start <= 12; start += periodCadence) {
        const deadline = documentDeadline(
          { applicableMonth: MONTH_CODES[start - 1], applicableYear: year, renewalType: deadlineType, renewalInterval: deadlineInterval },
          1,
        );
        if (!deadline || deadline >= todayKey || deadline < `${currentYear}-01-01`) continue;
        const end = start + periodCadence - 1;
        const periodEnd = format(new Date(year, end, 0), 'yyyy-MM-dd');
        if (sinceKey && periodEnd < sinceKey) continue;
        let delivered = false;
        for (let month = start; month <= end; month++) {
          if (deliveredMonths.has(`${year}-${String(month).padStart(2, '0')}`)) delivered = true;
        }
        if (delivered) continue;
        items.push({
          key: `${definition.id}:${year}-${start}:overdue`,
          definitionId: definition.id,
          documentName: definition.name,
          period: periodLabel(periodCadence, year, start),
          status: 'overdue',
          reason: null,
          targetMonth: targetMonthFor(periodCadence, year, start),
          deadline,
        });
      }
    }
  });

  return items.sort((a, b) =>
    (SEVERITY[a.status] ?? 9) - (SEVERITY[b.status] ?? 9) ||
    a.targetMonth.localeCompare(b.targetMonth) ||
    a.documentName.localeCompare(b.documentName, 'es'),
  );
}
